import React, { FC, memo } from 'react';
import { useMutation } from 'react-query';
import { ExclamationCircleOutlined } from '@ant-design/icons/lib';
import { Button, message, Modal } from 'antd';
import { ButtonProps } from 'antd/lib/button';
import { MedicalOperator } from 'models/medicalOperator';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { useEnsureContext } from 'common/hooks';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { FormattedMessage } from 'react-intl';

export interface DeletePatientProps
  extends Omit<ButtonProps, 'onClick' | 'loading' | 'href'> {
  patientsIds: number[];
  onDeleted?: () => void;
  medicalOperatorId: number;
  medicalOperator: MedicalOperator;
}

export const RemoveAssignedPatientsButton: FC<DeletePatientProps> = memo(
  ({
    patientsIds,
    onDeleted,
    medicalOperatorId,
    medicalOperator,
    ...buttonProps
  }) => {
    const bpContext = useEnsureContext(BusinessPartnerContext);
    const [mutate, { isLoading }] = useMutation(
      BusinessPartnersAPI.unassignMedicalOperatorPatients
    );
    const intlMessage = useFormatMessage();

    const confirm = () =>
      Modal.confirm({
        title: intlMessage({ id: 'patients.removeFromList.confirmation' }),
        icon: <ExclamationCircleOutlined />,
        okText: intlMessage({ id: 'yes' }),
        cancelText: intlMessage({ id: 'cancel' }),
        okType: 'danger',
        onOk: () =>
          mutate(
            {
              businessPartnerId: medicalOperator?.partnerDetails
                ? medicalOperator.partnerDetails.id
                : bpContext.id,
              dto: { items: patientsIds, medicalOperatorId },
            },
            {
              onSuccess: () => {
                message.success(
                  intlMessage({ id: 'patients.removeFromList.success' })
                );
                onDeleted && onDeleted();
              },
            }
          ),
      });

    return (
      <Button
        id="button-patients-unassign"
        loading={isLoading}
        type="primary"
        danger
        onClick={() => confirm()}
        {...buttonProps}
      >
        <FormattedMessage id="patients.unassign" tagName="span" />
      </Button>
    );
  }
);
